/**
 * Camera Lucida - Main Application
 *
 * A Da Vinci-style drawing aid app using marker detection and Canvas 2D overlay.
 * Uses js-aruco for ArUco marker detection with detect-once-then-lock workflow.
 */

import { HUD } from './controls/hud';
import { appStore, imageStore } from './storage/app-state';
import { ImageStore } from './storage/image-store';
import { GestureHandler } from './utils/gestures';
import { applyFilter, FilterType } from './utils/filters';
import { createArtistBrowser, type ArtistBrowserCallbacks } from './components/artist-browser';
import type { DrawingSource } from './data/drawing-compendium';
import { CanvasOverlay, createCanvasOverlay } from './render/canvas-overlay';
import { ArucoDetector, getArucoDetector, type DetectedMarker } from './cv/aruco-detector';

// App state types
export type AppState = 'IDLE' | 'MARKER_ONLY' | 'IMAGE_ONLY' | 'POSITIONING' | 'DRAWING';

// Telemetry logging (for adb logcat / device testing)
function logTelemetry(event: string, data: Record<string, unknown>): void {
  console.log(`[DOPPLE_TELEMETRY] ${event}: ${JSON.stringify(data)}`);
}

// Expose imageStore for debugging and e2e tests
(window as Window & { imageStore?: typeof imageStore }).imageStore = imageStore;

// DOM Elements
const appContainer = document.getElementById('hud-container')!;
const galleryModal = document.getElementById('gallery-modal')!;
const galleryClose = document.getElementById('gallery-close')!;
const errorOverlay = document.getElementById('error-overlay')!;
const errorMessage = document.getElementById('error-message')!;
const errorRetry = document.getElementById('error-retry')!;
const stateIndicator = document.getElementById('state-indicator')!;
const stateText = document.getElementById('state-text')!;
const markerHint = document.getElementById('marker-hint')!;
const placeholderFrame = document.getElementById('placeholder-frame')!;
const cameraFeed = document.getElementById('camera-feed') as HTMLVideoElement;
// overlayCanvas is used by canvasOverlay via createCanvasOverlay('overlay-canvas')

// Gallery tabs and artist browser
const galleryTabs = document.querySelectorAll('.gallery-tab');
const mastersTab = document.getElementById('masters-tab')!;
const favoritesTab = document.getElementById('favorites-tab')!;
const favoritesGrid = document.getElementById('favorites-grid')!;
const favoritesEmpty = document.querySelector('.favorites-empty')!;
const artistBrowserContainer = document.getElementById('artist-browser')!;

// Favorites storage key
const FAVORITES_KEY = 'camera-lucida-favorites';

// Get favorites from localStorage
function getFavorites(): DrawingSource[] {
  try {
    const stored = localStorage.getItem(FAVORITES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

// Save favorites to localStorage
function saveFavorites(favorites: DrawingSource[]): void {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
}

// Check if a drawing is favorited
function isFavorite(drawing: DrawingSource): boolean {
  const favorites = getFavorites();
  return favorites.some(f => f.url === drawing.url);
}

// Toggle favorite status
function toggleFavorite(drawing: DrawingSource): void {
  const favorites = getFavorites();
  const index = favorites.findIndex(f => f.url === drawing.url);
  if (index >= 0) {
    favorites.splice(index, 1);
  } else {
    favorites.push(drawing);
  }
  saveFavorites(favorites);
}

// Artist browser instance
let artistBrowser: ReturnType<typeof createArtistBrowser> | null = null;

// Canvas overlay for image rendering
let canvasOverlay: CanvasOverlay | null = null;

// ArUco marker detector
let arucoDetector: ArucoDetector | null = null;
let detectedMarker: DetectedMarker | null = null;
let detectionAnimationId: number | null = null;

// Current app state
let currentState: AppState = 'IDLE';

// HUD visibility from query param
const urlParams = new URLSearchParams(window.location.search);
const hudParam = urlParams.get('hud');
const hudVisible = hudParam !== 'false' && hudParam !== '0';
const debugMode = urlParams.has('debug');

// Storage
const persistentImageStore = new ImageStore();

// State
let strobeInterval: number | null = null;

// Filter cycle order
const FILTER_CYCLE: FilterType[] = ['none', 'grayscale', 'edges'];

// State machine
function setState(newState: AppState): void {
  if (currentState === newState) return;

  const prevState = currentState;
  currentState = newState;

  console.log(`[CameraLucida] State: ${prevState} -> ${newState}`);

  // Update UI based on state
  updateStateUI();

  // Log telemetry
  logTelemetry('state_change', { from: prevState, to: newState });
}

function updateStateUI(): void {
  // Update state indicator (debug mode only)
  if (debugMode) {
    stateIndicator.classList.remove('hidden');
    stateText.textContent = currentState;
  }

  // Update marker hint visibility
  const showHint = currentState === 'IDLE' || currentState === 'IMAGE_ONLY';
  markerHint.classList.toggle('hidden', !showHint);

  // Update placeholder frame (marker detected, no image)
  const showPlaceholder = currentState === 'MARKER_ONLY';
  placeholderFrame.classList.toggle('hidden', !showPlaceholder);

  // Update lock indicator and drawing mode
  const isDrawing = currentState === 'DRAWING';
  updateLockIndicator(isDrawing);
  hud.setDrawingMode(isDrawing);
}

// Initialize HUD
const hud = new HUD(appContainer, {
  onOpacityChange: (value) => {
    imageStore.setState({ opacity: value });
    canvasOverlay?.setOpacity(value);
  },
  onGridToggle: () => {
    const { gridEnabled } = imageStore.getState();
    const newGridEnabled = !gridEnabled;
    imageStore.setState({ gridEnabled: newGridEnabled });
    hud.setGridEnabled(newGridEnabled);
    // Grid overlay not implemented in Canvas 2D mode yet
  },
  onFilterCycle: () => {
    const { filter } = imageStore.getState();
    const currentIndex = FILTER_CYCLE.indexOf(filter);
    const nextFilter = FILTER_CYCLE[(currentIndex + 1) % FILTER_CYCLE.length];
    imageStore.setState({ filter: nextFilter });
    hud.setFilter(nextFilter);
    applyImageFilter(nextFilter);
  },
  onStrobeToggle: () => {
    const { strobeEnabled } = appStore.getState();
    appStore.setState({ strobeEnabled: !strobeEnabled });
    toggleStrobe(!strobeEnabled);
  },
  onLockToggle: () => {
    const { isLocked } = appStore.getState();
    const newLocked = !isLocked;
    appStore.setState({ isLocked: newLocked });
    hud.setLocked(newLocked);

    // Transition state based on lock
    if (newLocked && currentState === 'POSITIONING') {
      setState('DRAWING');
    } else if (!newLocked && currentState === 'DRAWING') {
      setState('POSITIONING');
    }

    logTelemetry('lock_mode', { enabled: newLocked });
  },
  onAddImage: () => {
    openGallery();
  },
  onPositionChange: (dx, dy) => {
    if (appStore.getState().isLocked) return;

    // Apply position offset to canvas overlay
    const scale = 2; // Adjust for joystick sensitivity
    canvasOverlay?.applyOffset(dx * scale, dy * scale);

    // Also update image store for persistence
    const state = imageStore.getState();
    const offsetScale = 0.002;
    imageStore.setState({
      offset: {
        x: state.offset.x + dx * offsetScale,
        y: state.offset.y - dy * offsetScale,
        z: state.offset.z,
      },
    });
  },
  onScaleChange: (delta) => {
    if (appStore.getState().isLocked) return;

    const multiplier = delta > 0 ? 1.15 : 0.87;
    canvasOverlay?.applyScale(multiplier);

    // Update image store for persistence
    const state = imageStore.getState();
    const newScale = Math.max(0.1, Math.min(50, state.scale * multiplier));
    imageStore.setState({ scale: newScale });
  },
  onFlipH: () => {
    const { flipH } = imageStore.getState();
    imageStore.setState({ flipH: !flipH });
    hud.setFlipH(!flipH);
    canvasOverlay?.toggleFlipH();
  },
  onFlipV: () => {
    const { flipV } = imageStore.getState();
    imageStore.setState({ flipV: !flipV });
    hud.setFlipV(!flipV);
    canvasOverlay?.toggleFlipV();
  },
  onRotationChange: (delta) => {
    if (appStore.getState().isLocked) return;

    if (delta === 0) {
      // Reset rotation
      canvasOverlay?.setTransform({ rotation: 0 });
    } else {
      // Apply rotation (convert degrees to radians)
      canvasOverlay?.applyRotation(delta * Math.PI / 180);
    }
  },
});

// Initialize gesture handler (pinch for scale, double-tap for reset)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _gestureHandler = new GestureHandler(appContainer, {
  onDrag: () => {
    // Drag handled by joystick
  },
  onPinch: (scale) => {
    if (appStore.getState().isLocked) return;

    canvasOverlay?.applyScale(scale);

    const state = imageStore.getState();
    const newScale = Math.max(0.1, Math.min(50, state.scale * scale));
    imageStore.setState({ scale: newScale });
  },
  onDoubleTap: () => {
    // In DRAWING mode, double-tap unlocks
    if (currentState === 'DRAWING') {
      appStore.setState({ isLocked: false });
      hud.setLocked(false);
      setState('POSITIONING');
      return;
    }

    // In other modes, reset position and scale
    canvasOverlay?.resetTransform();
    imageStore.setState({
      offset: { x: 0, y: 0, z: 0 },
      scale: 1,
    });
  },
});

// Gallery functions
function openGallery(): void {
  if (!artistBrowser) {
    const callbacks: ArtistBrowserCallbacks = {
      onSelectDrawing: (drawing: DrawingSource) => selectCompendiumDrawing(drawing),
      onClose: closeGallery,
      onToggleFavorite: toggleFavorite,
      isFavorite: isFavorite,
    };
    artistBrowser = createArtistBrowser(artistBrowserContainer, callbacks);
  } else {
    artistBrowser.refresh();
  }

  refreshFavoritesGrid();
  galleryModal.classList.remove('hidden');
}

function closeGallery(): void {
  galleryModal.classList.add('hidden');
}

// Load image from path (for query param preselection)
async function loadImageFromPath(path: string): Promise<void> {
  try {
    console.log(`[CameraLucida] Loading preselected image: ${path}`);
    const response = await fetch(path);
    if (!response.ok) {
      console.error(`Failed to load preselected image: ${path} (${response.status})`);
      return;
    }

    const blob = await response.blob();
    const filename = path.split('/').pop() || 'Preselected';
    const displayName = filename.replace(/\.[^.]+$/, '').replace(/-/g, ' ');

    const thumbnail = await createThumbnailFromBlob(blob, 200);
    const savedImage = await persistentImageStore.saveImage(displayName, blob, thumbnail);

    await selectImage(savedImage.id);
    console.log(`[CameraLucida] Preselected image loaded: ${displayName}`);
  } catch (error) {
    console.error('Failed to load preselected image:', error);
  }
}

// Select drawing from compendium (local files)
async function selectCompendiumDrawing(drawing: DrawingSource): Promise<void> {
  try {
    const response = await fetch(drawing.url);
    if (!response.ok) {
      console.error(`Failed to load drawing: ${drawing.url}`);
      return;
    }

    const blob = await response.blob();
    const displayName = `${drawing.title} (${drawing.artist})`;

    const existingImages = await persistentImageStore.getAllImages();
    let existingImage = existingImages.find(img => img.name === displayName);

    if (!existingImage) {
      const thumbnail = await createThumbnailFromBlob(blob, 200);
      existingImage = await persistentImageStore.saveImage(displayName, blob, thumbnail);
    }

    await selectImage(existingImage.id);
  } catch (error) {
    console.error('Failed to load compendium drawing:', error);
  }
}

// Create thumbnail from blob
async function createThumbnailFromBlob(blob: Blob, maxSize: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;

      const scale = Math.min(maxSize / img.width, maxSize / img.height);
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (result) => {
          if (result) resolve(result);
          else reject(new Error('Failed to create thumbnail'));
        },
        'image/jpeg',
        0.8
      );
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(blob);
  });
}

// Tab switching
function switchGalleryTab(tabName: string): void {
  galleryTabs.forEach(tab => {
    const isActive = tab.getAttribute('data-tab') === tabName;
    tab.classList.toggle('active', isActive);
  });

  mastersTab.classList.toggle('active', tabName === 'masters');
  favoritesTab.classList.toggle('active', tabName === 'favorites');

  if (tabName === 'favorites') {
    refreshFavoritesGrid();
  }
}

// Refresh favorites grid
function refreshFavoritesGrid(): void {
  const favorites = getFavorites();

  if (favorites.length === 0) {
    favoritesGrid.innerHTML = '';
    favoritesEmpty.classList.remove('hidden');
    return;
  }

  favoritesEmpty.classList.add('hidden');
  favoritesGrid.innerHTML = '';

  for (const drawing of favorites) {
    const item = document.createElement('div');
    item.className = 'gallery-item';

    const img = document.createElement('img');
    img.src = drawing.thumbUrl || drawing.url;
    img.alt = drawing.title;
    img.loading = 'lazy';
    img.onerror = () => { item.style.display = 'none'; };

    item.appendChild(img);

    const label = document.createElement('div');
    label.className = 'gallery-item-label';
    const shortArtist = drawing.artist.split(' ').pop() || drawing.artist;
    label.textContent = shortArtist;
    item.appendChild(label);

    const removeBtn = document.createElement('button');
    removeBtn.className = 'favorite-remove-btn';
    removeBtn.innerHTML = '×';
    removeBtn.title = 'Remove from favorites';
    removeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleFavorite(drawing);
      refreshFavoritesGrid();
      artistBrowser?.refresh();
    });
    item.appendChild(removeBtn);

    item.addEventListener('click', () => selectCompendiumDrawing(drawing));
    favoritesGrid.appendChild(item);
  }
}

async function selectImage(id: string): Promise<void> {
  console.log(`[CameraLucida] selectImage called with id: ${id}`);
  const img = await persistentImageStore.getImage(id);
  if (!img) {
    console.error(`[CameraLucida] selectImage: image not found for id: ${id}`);
    return;
  }

  imageStore.setState({
    currentImage: { id: img.id, blob: img.blob, name: img.name },
    opacity: img.settings.opacity,
    filter: img.settings.filter,
    offset: img.settings.offset,
    scale: img.settings.scale,
    gridEnabled: img.settings.gridEnabled,
  });

  // Load image to canvas overlay
  await loadImageToCanvas(img.blob, img.settings.filter);

  // Apply stored settings to overlay
  canvasOverlay?.setOpacity(img.settings.opacity);

  // Update HUD
  hud.setOpacity(img.settings.opacity);
  hud.setFilter(img.settings.filter);
  hud.setGridEnabled(img.settings.gridEnabled);

  // Update state
  if (currentState === 'IDLE') {
    setState('IMAGE_ONLY');
  } else if (currentState === 'MARKER_ONLY') {
    setState('POSITIONING');
  }

  closeGallery();
}

// Load image to canvas with optional filter
async function loadImageToCanvas(blob: Blob, filter: FilterType = 'none'): Promise<void> {
  if (!canvasOverlay) return;

  // Load image
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = URL.createObjectURL(blob);
  });

  // Create canvas for processing
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  // Cap at 2048
  const maxSize = 2048;
  let width = img.width;
  let height = img.height;

  if (width > maxSize || height > maxSize) {
    const scale = Math.min(maxSize / width, maxSize / height);
    width *= scale;
    height *= scale;
  }

  canvas.width = width;
  canvas.height = height;
  ctx.drawImage(img, 0, 0, width, height);

  // Apply filter if needed
  if (filter !== 'none') {
    const imageData = ctx.getImageData(0, 0, width, height);
    const filtered = applyFilter(imageData, filter);
    ctx.putImageData(filtered, 0, 0);
  }

  // Set the processed canvas as the overlay image
  canvasOverlay.setImage(canvas);

  console.log(`[CameraLucida] Image loaded to canvas: ${width}x${height}, filter=${filter}`);
}

async function applyImageFilter(filter: FilterType): Promise<void> {
  const { currentImage } = imageStore.getState();
  if (!currentImage?.blob) return;

  await loadImageToCanvas(currentImage.blob, filter);
}

// Lock indicator
function updateLockIndicator(locked: boolean): void {
  const indicator = document.getElementById('lock-indicator');
  if (indicator) {
    indicator.classList.toggle('hidden', !locked);
  }
}

// Strobe mode
function toggleStrobe(enabled: boolean): void {
  hud.setStrobeEnabled(enabled);

  if (enabled) {
    const { strobeInterval: interval } = appStore.getState();
    let visible = true;
    strobeInterval = window.setInterval(() => {
      visible = !visible;
      canvasOverlay?.setVisible(visible);
    }, interval);
  } else {
    if (strobeInterval) {
      clearInterval(strobeInterval);
      strobeInterval = null;
    }
    canvasOverlay?.setVisible(true);
  }
}

// Error handling
function showError(message: string): void {
  errorMessage.textContent = message;
  errorOverlay.classList.remove('hidden');
}

function hideError(): void {
  errorOverlay.classList.add('hidden');
}

// Event listeners
galleryClose.addEventListener('click', closeGallery);

// Placeholder frame opens gallery when clicked
placeholderFrame.addEventListener('click', openGallery);

galleryTabs.forEach(tab => {
  tab.addEventListener('click', () => {
    const tabName = tab.getAttribute('data-tab');
    if (tabName) {
      switchGalleryTab(tabName);
    }
  });
});

errorRetry.addEventListener('click', () => {
  hideError();
  window.location.reload();
});

// Save settings when changed
imageStore.subscribe((state) => {
  if (state.currentImage) {
    persistentImageStore.updateImageSettings(state.currentImage.id, {
      opacity: state.opacity,
      filter: state.filter,
      offset: state.offset,
      scale: state.scale,
      gridEnabled: state.gridEnabled,
    });
  }
});

// Global state and test API
declare global {
  interface Window {
    __cameraLucidaInitialized?: boolean;
    __CAMERA_LUCIDA_TEST__?: TestAPI;
  }
}

interface ImageTransform {
  x: number;
  y: number;
  scale: number;
  rotation: number;
  flipH: boolean;
  flipV: boolean;
}

interface MarkerPoseInfo {
  id: number;
  center: { x: number; y: number };
  size: number;
  rotation: number;
}

interface TestAPI {
  // State
  getState: () => AppState;
  setState: (state: AppState) => void;

  // Detection simulation
  simulateMarkerDetected: () => void;
  simulateMarkerLost: () => void;

  // Mock camera (for testing without real camera)
  enableMockCamera: () => void;
  disableMockCamera: () => void;

  // Image
  selectImage: (index: number) => Promise<void>;
  getImageTransform: () => ImageTransform | null;

  // Controls
  getOpacity: () => number;
  setOpacity: (value: number) => void;
  lock: () => void;
  unlock: () => void;

  // Assertions
  isMarkerVisible: () => boolean;
  getDetectedPose: () => MarkerPoseInfo | null;

  // Detection control
  startDetection: () => void;
  stopDetection: () => void;
}

// Initialize camera
async function initCamera(): Promise<void> {
  try {
    console.log('[CameraLucida] Requesting camera access...');

    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 1080 },
        height: { ideal: 1080 },
        facingMode: 'environment',
      },
    });

    cameraFeed.srcObject = stream;
    await cameraFeed.play();

    const track = stream.getVideoTracks()[0];
    const settings = track.getSettings();
    console.log(`[CameraLucida] Camera started: ${settings.width}x${settings.height}`);

    // Resize canvas to match video
    const size = Math.min(settings.width || 800, settings.height || 800);
    canvasOverlay?.resize(size, size);

    // Initialize ArUco detector with camera parameters
    arucoDetector = getArucoDetector({
      markerSizeMm: 50, // Assume 50mm markers
      focalLength: size, // Approximate focal length
    });

    // Start marker detection loop
    startMarkerDetection();

  } catch (error) {
    console.error('[CameraLucida] Camera access failed:', error);
    showError('Camera access is required. Please grant camera permission and reload.');
  }
}

// Marker detection loop
function startMarkerDetection(): void {
  if (detectionAnimationId !== null) return;

  const detectFrame = () => {
    // Skip detection in DRAWING state (locked)
    if (currentState === 'DRAWING') {
      detectionAnimationId = requestAnimationFrame(detectFrame);
      return;
    }

    // Detect markers from video
    if (arucoDetector && cameraFeed.videoWidth > 0) {
      const markers = arucoDetector.detectFromVideo(cameraFeed);

      if (markers.length > 0) {
        // Found marker(s) - use the first one
        const marker = markers[0];

        if (!detectedMarker || detectedMarker.id !== marker.id) {
          console.log(`[CameraLucida] Marker detected: ID=${marker.id}, center=(${marker.center.x.toFixed(0)}, ${marker.center.y.toFixed(0)})`);
        }

        detectedMarker = marker;

        // Update state based on marker detection
        if (currentState === 'IDLE') {
          setState('MARKER_ONLY');
        } else if (currentState === 'IMAGE_ONLY') {
          setState('POSITIONING');
        }

        // Update marker hint
        markerHint.classList.add('hidden');

        // Position the overlay based on marker (if in POSITIONING state)
        if (currentState === 'POSITIONING' && canvasOverlay) {
          // Convert marker center to canvas coordinates
          const scaleX = canvasOverlay.getDimensions().width / cameraFeed.videoWidth;
          const scaleY = canvasOverlay.getDimensions().height / cameraFeed.videoHeight;

          canvasOverlay.setTransform({
            x: marker.center.x * scaleX,
            y: marker.center.y * scaleY,
            rotation: marker.rotation,
            scale: marker.size / 100, // Normalize scale
          });
        }
      } else {
        // No markers detected
        if (detectedMarker) {
          console.log('[CameraLucida] Marker lost');
          detectedMarker = null;
        }

        // Update state on marker loss (only if not locked)
        if (currentState === 'MARKER_ONLY') {
          setState('IDLE');
        } else if (currentState === 'POSITIONING') {
          // Keep state but show hint
          markerHint.classList.remove('hidden');
          markerHint.querySelector('span')!.textContent = 'Marker lost - point camera at marker';
        }
      }
    }

    detectionAnimationId = requestAnimationFrame(detectFrame);
  };

  detectionAnimationId = requestAnimationFrame(detectFrame);
}

// Stop marker detection (exported for test API)
export function stopMarkerDetection(): void {
  if (detectionAnimationId !== null) {
    cancelAnimationFrame(detectionAnimationId);
    detectionAnimationId = null;
  }
}

// Mock camera state
let mockCameraEnabled = false;

// Expose test API
function exposeTestAPI(): void {
  window.__CAMERA_LUCIDA_TEST__ = {
    // State
    getState: () => currentState,
    setState: (state: AppState) => setState(state),

    // Detection simulation
    simulateMarkerDetected: () => {
      // Create a fake detected marker for testing
      detectedMarker = {
        id: 0,
        corners: [
          { x: 300, y: 300 },
          { x: 500, y: 300 },
          { x: 500, y: 500 },
          { x: 300, y: 500 },
        ],
        center: { x: 400, y: 400 },
        size: 200,
        rotation: 0,
      };

      if (currentState === 'IDLE') {
        setState('MARKER_ONLY');
      } else if (currentState === 'IMAGE_ONLY') {
        setState('POSITIONING');
      }
    },
    simulateMarkerLost: () => {
      detectedMarker = null;

      if (currentState === 'POSITIONING') {
        setState('IMAGE_ONLY');
      } else if (currentState === 'MARKER_ONLY') {
        setState('IDLE');
      }
    },

    // Mock camera
    enableMockCamera: () => {
      mockCameraEnabled = true;
      stopMarkerDetection();
      console.log('[CameraLucida] Mock camera enabled');
    },
    disableMockCamera: () => {
      mockCameraEnabled = false;
      startMarkerDetection();
      console.log('[CameraLucida] Mock camera disabled');
    },

    // Image
    selectImage: async (index: number) => {
      const images = await persistentImageStore.getAllImages();
      if (images[index]) {
        await selectImage(images[index].id);
      }
    },
    getImageTransform: () => {
      if (!canvasOverlay) return null;
      const transform = canvasOverlay.getTransform();
      return {
        x: transform.x,
        y: transform.y,
        scale: transform.scale,
        rotation: transform.rotation,
        flipH: transform.flipH,
        flipV: transform.flipV,
      };
    },

    // Controls
    getOpacity: () => canvasOverlay?.getOpacity() ?? 0.5,
    setOpacity: (value: number) => {
      canvasOverlay?.setOpacity(value);
      imageStore.setState({ opacity: value });
      hud.setOpacity(value);
    },
    lock: () => {
      if (currentState === 'POSITIONING') {
        appStore.setState({ isLocked: true });
        hud.setLocked(true);
        setState('DRAWING');
      }
    },
    unlock: () => {
      if (currentState === 'DRAWING') {
        appStore.setState({ isLocked: false });
        hud.setLocked(false);
        setState('POSITIONING');
      }
    },

    // Assertions
    isMarkerVisible: () => detectedMarker !== null,
    getDetectedPose: () => {
      if (!detectedMarker) return null;
      return {
        id: detectedMarker.id,
        center: detectedMarker.center,
        size: detectedMarker.size,
        rotation: detectedMarker.rotation,
      };
    },

    // Detection control
    startDetection: () => startMarkerDetection(),
    stopDetection: () => stopMarkerDetection(),
  };
}

// Main initialization
async function init(): Promise<void> {
  if (window.__cameraLucidaInitialized) {
    console.log('[CameraLucida] Already initialized, skipping HMR re-init');
    return;
  }
  window.__cameraLucidaInitialized = true;

  try {
    console.log('[CameraLucida] Initializing...');

    // Create canvas overlay
    canvasOverlay = createCanvasOverlay('overlay-canvas');
    console.log('[CameraLucida] Canvas overlay created');

    // Initialize camera
    await initCamera();

    // Apply HUD visibility from query param
    if (!hudVisible) {
      hud.setControlsVisible(false);
      console.log('[CameraLucida] HUD controls hidden via query param');
    }

    // Expose test API
    exposeTestAPI();

    // Load preselected or last used image
    const preselectedImage = (window as Window & { preselectedImage?: string }).preselectedImage;
    if (preselectedImage) {
      await loadImageFromPath(preselectedImage);
    } else {
      const images = await persistentImageStore.getAllImages();
      if (images.length > 0) {
        images.sort((a, b) => b.lastUsedAt - a.lastUsedAt);
        await selectImage(images[0].id);
      }
    }

    // Hide loading indicator
    const loader = document.getElementById('loading-indicator');
    if (loader) {
      loader.style.opacity = '0';
      setTimeout(() => loader.remove(), 300);
    }

    // Mark timing
    const win = window as unknown as { markTiming?: (label: string) => void };
    if (win.markTiming) {
      win.markTiming('appInitialized');
    }

    console.log('[CameraLucida] Initialization complete');

  } catch (error) {
    console.error('[CameraLucida] Initialization failed:', error);
    showError('Failed to initialize the app. Please reload.');
  }
}

// Handle keyboard shortcuts
document.addEventListener('keydown', (e: KeyboardEvent) => {
  if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
    console.log('[CameraLucida] Ctrl+C pressed - notifying parent');
    e.preventDefault();
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'camera-lucida:exit' }, '*');
    }
  }
});

// Start the app
init();
